import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories, categoryIcons } from '@/mocks/categories';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Spreadsheet', href: '/#spreadsheet' },
  { label: 'Kategorie', href: '/#categories' },
  { label: 'Poradniki', href: '/#guides' },
  { label: 'Bezpieczeństwo', href: '/#safety' },
  { label: 'FAQ', href: '/#faq' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    if (href.startsWith('/#')) {
      const id = href.replace('/#', '');
      if (location.pathname === '/') {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigate(href);
      }
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-950 hover:text-primary-600 transition-colors duration-200"
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-store-2-line text-xl text-primary-500"></i>
            </div>
            <span className="font-heading text-lg font-semibold whitespace-nowrap">
              Kakobuy <span className="text-primary-500">Polska</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            {navLinks.map((link) =>
              link.href.startsWith('/#') ? (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  {link.label}
                </button>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
                >
                  {link.label}
                </Link>
              )
            )}

            <Link
              to="/artykul/kakobuy-spreadsheet-polska-kompletny-przewodnik"
              className="text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
            >
              Poradnik zakupowy
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-5 py-2.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Kakobuy Spreadsheet
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) =>
              link.href.startsWith('/#') ? (
                <button
                  key={link.href}
                  onClick={() => handleNavClick(link.href)}
                  className="flex items-center gap-3 text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 py-3 px-2 rounded-lg hover:bg-background-100 text-left cursor-pointer"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100">
                    <i className="ri-arrow-right-s-line text-foreground-400"></i>
                  </div>
                  {link.label}
                </button>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className="flex items-center gap-3 text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 py-3 px-2 rounded-lg hover:bg-background-100"
                  onClick={() => setMobileOpen(false)}
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100">
                    <i className="ri-home-4-line text-foreground-400"></i>
                  </div>
                  {link.label}
                </Link>
              )
            )}

            <Link
              to="/artykul/kakobuy-spreadsheet-polska-kompletny-przewodnik"
              className="flex items-center gap-3 text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 py-3 px-2 rounded-lg hover:bg-background-100"
              onClick={() => setMobileOpen(false)}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-background-100">
                <i className="ri-book-open-line text-foreground-400"></i>
              </div>
              Poradnik zakupowy
            </Link>

            <div className="my-2 border-t border-background-200/70"></div>

            <button
              onClick={() => setMobileCategoriesOpen(!mobileCategoriesOpen)}
              className="flex items-center justify-between gap-3 text-sm font-semibold text-foreground-900 py-3 px-2 rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer w-full text-left"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100">
                  <i className="ri-grid-line text-accent-500"></i>
                </div>
                Kategorie produktów
              </div>
              <div className={`w-5 h-5 flex items-center justify-center transition-transform duration-300 ${mobileCategoriesOpen ? 'rotate-180' : ''}`}>
                <i className="ri-arrow-down-s-line text-foreground-400"></i>
              </div>
            </button>

            {mobileCategoriesOpen && (
              <div className="ml-4 pl-4 border-l-2 border-background-200/70 animate-[fadeIn_0.3s_ease-out]">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 text-sm text-foreground-600 hover:text-primary-500 transition-colors duration-200 py-2.5 px-2 rounded-lg hover:bg-background-100"
                    onClick={() => setMobileOpen(false)}
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100">
                      <i className={`${categoryIcons[cat.name] || 'ri-store-2-line'} text-primary-500 text-xs`}></i>
                    </div>
                    {cat.name}
                  </a>
                ))}
              </div>
            )}

            <div className="my-2 border-t border-background-200/70"></div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-5 py-3 rounded-full text-center transition-all duration-200 whitespace-nowrap"
              onClick={() => setMobileOpen(false)}
            >
              Kakobuy Spreadsheet
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}